import {
  WebhookFailureAction,
  WebhookNotifyChannel,
  type CallWebhookActionData,
  type RetryPolicy,
} from '@/proto/logiblocks/logiblocks';
import type { CredentialAuthType } from '@/apis/customerCredentials';

/**
 * "None" plus the two credential auth types -- a pure client-side filter over
 * the fetched credential list (narrows which credentials the Credential
 * dropdown offers). Not part of CallWebhookActionData: the referenced
 * Credential already knows its own auth_type, so persisting a second copy
 * here would just be a value that can drift from it.
 */
export type AuthTypeFilter = 'NONE' | CredentialAuthType;

export const AUTH_TYPE_FILTER_OPTIONS: {
  label: string;
  value: AuthTypeFilter;
}[] = [
  { label: 'None', value: 'NONE' },
  { label: 'API keys', value: 'API_KEY' },
  { label: 'OAuth', value: 'OAUTH_CLIENT_CREDENTIALS' },
];

export const NO_RETRY_POLICY: RetryPolicy = {
  max_attempts: 1,
  initial_backoff_seconds: 0,
  backoff_multiplier: 0,
  max_backoff_seconds: 0,
};

/**
 * "Retry once" -- the PRD's default. A single retry absorbs the common case
 * (a blip at the endpoint) without letting a genuinely broken endpoint hold a
 * run open for minutes.
 */
export const DEFAULT_RETRY_POLICY: RetryPolicy = {
  max_attempts: 2,
  initial_backoff_seconds: 10,
  backoff_multiplier: 4,
  max_backoff_seconds: 600,
};

/** Methods that carry no request body; the Body field is hidden for these. */
export const METHODS_WITHOUT_BODY = ['GET'];

export const CONTENT_TYPE_HEADER = 'Content-Type';
export const DEFAULT_CONTENT_TYPE = 'application/json';

/**
 * Pre-filled into a new node's body so a builder sees the expected shape
 * rather than an empty box, per the PRD.
 *
 * Deliberately references no variables. A starter that named e.g.
 * {{trigger.shipment_id}} would be an invalid reference under any trigger
 * that does not publish it -- a schedule trigger publishes nothing at all --
 * so a brand-new node would fail validation before being touched. The keys
 * show the shape; the builder fills the values from the variable picker.
 */
export const STARTER_BODY_TEMPLATE = [
  '{',
  '  "shipment_id": "",',
  '  "status": ""',
  '}',
].join('\n');

export const DEFAULT_CALL_WEBHOOK_ACTION: CallWebhookActionData = {
  method: 'POST',
  url_chunks: [],
  // Content-Type is a real, editable header row rather than a hidden field,
  // so the panel shows every header that will actually be sent.
  headers: [
    {
      key: CONTENT_TYPE_HEADER,
      value_chunks: [{ text: DEFAULT_CONTENT_TYPE }],
    },
  ],
  body_chunks: [{ text: STARTER_BODY_TEMPLATE }],
  content_type: DEFAULT_CONTENT_TYPE,
  credential_id: '',
  retry_policy: DEFAULT_RETRY_POLICY,
  on_failure: WebhookFailureAction.WEBHOOK_FAILURE_CONTINUE,
  notify_channel: WebhookNotifyChannel.WEBHOOK_NOTIFY_IN_APP,
};

export const NOTIFY_CHANNEL_OPTIONS: {
  label: string;
  value: WebhookNotifyChannel;
}[] = [
  {
    label: 'Automation owner (in-app)',
    value: WebhookNotifyChannel.WEBHOOK_NOTIFY_IN_APP,
  },
  {
    label: 'Automation owner (email)',
    value: WebhookNotifyChannel.WEBHOOK_NOTIFY_EMAIL,
  },
];

/**
 * The 4 retry options the customization panel exposes, mapped to the concrete
 * RetryPolicy the backend runs. Backoff is the PRD's schedule -- 10s, 40s,
 * 160s, capped at 10 minutes between attempts. Only `max_attempts` is
 * user-facing; the rest are fixed per preset so the backend is
 * exponential-backoff-ready without shipping a tuning UI yet.
 */
export const RETRY_POLICY_PRESETS: { label: string; value: RetryPolicy }[] = [
  { label: 'No retries', value: NO_RETRY_POLICY },
  {
    label: 'Retry once',
    value: {
      max_attempts: 2,
      initial_backoff_seconds: 10,
      backoff_multiplier: 4,
      max_backoff_seconds: 600,
    },
  },
  {
    label: 'Retry 3x',
    value: {
      max_attempts: 4,
      initial_backoff_seconds: 10,
      backoff_multiplier: 4,
      max_backoff_seconds: 600,
    },
  },
  {
    label: 'Retry 5x',
    value: {
      max_attempts: 6,
      initial_backoff_seconds: 10,
      backoff_multiplier: 4,
      max_backoff_seconds: 600,
    },
  },
];

export const ON_FAILURE_OPTIONS: {
  label: string;
  value: WebhookFailureAction;
}[] = [
  {
    label: 'Then continue silently',
    value: WebhookFailureAction.WEBHOOK_FAILURE_CONTINUE,
  },
  {
    label: 'Then notify',
    value: WebhookFailureAction.WEBHOOK_FAILURE_NOTIFY,
  },
];

export const METHOD_OPTIONS = ['GET', 'POST', 'PUT', 'PATCH'] as const;
